import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useSelector, useDispatch } from 'react-redux';
import { logout } from '../store/slices/authSlice';
import ActivityLogs from '../components/ActivityLogs';
import CreditTransferModal from '../components/CreditTransfer';
import PackageManager from './PackageManager';
import { StatsGrid, StatCard, LineChartWidget, BarChartWidget, PieChartWidget, AnalyticsGrid, generateMockData } from '../components/Charts';

const api = axios.create({
  baseURL: '/api/v1'
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('accessToken');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export default function SuperAdminDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);

  const handleLogout = () => {
    dispatch(logout());
    navigate('/super-admin/login');
  };
  const [paasList, setPaasList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showAdminModal, setShowAdminModal] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [paasSearchTerm, setPaasSearchTerm] = useState('');
  const [paasStatusFilter, setPaasStatusFilter] = useState('all');
  const [settingsSubTab, setSettingsSubTab] = useState('general');
  const [adminsList, setAdminsList] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [editingSubscription, setEditingSubscription] = useState(null);
  const [formData, setFormData] = useState({
    name: '', contactEmail: '', description: '', domain: '', primaryColor: '#1976d2',
    logo: '', favicon: '', appleIcon: '', termsUrl: '', privacyUrl: '', supportEmail: '',
    address: '', city: '', country: '', state: '', zipCode: '', companyName: '',
    plan: 'starter', maxSaaS: 10, maxTenants: 100, apiRateLimit: 1000,
    adminName: '', adminEmail: '', adminPassword: '',
    logoFile: null, logoFilePreview: null,
    faviconFile: null, faviconFilePreview: null,
    androidIconFile: null, androidIconFilePreview: null,
    iosIconFile: null, iosIconFilePreview: null
  });
  const [adminFormData, setAdminFormData] = useState({
    email: '', password: '', name: '', paasId: ''
  });
  const [editingAdmin, setEditingAdmin] = useState(null);
  const [editingPAAS, setEditingPAAS] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showTopupModal, setShowTopupModal] = useState(false);
  const [balance, setBalance] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [incomeData, setIncomeData] = useState(null);
  const [billingLoading, setBillingLoading] = useState(false);
  const [topupAmount, setTopupAmount] = useState(5000);
  const [showTransferModal, setShowTransferModal] = useState(false);

  const menuItems = [
    { id: 'overview', label: 'Overview', icon: '📊' },
    { id: 'paas', label: 'PAAS Platforms', icon: '🏢', subItems: [
      { id: 'all-platforms', label: 'All Platforms', path: '/super-admin/platforms' },
      { id: 'create-platform', label: 'Create Platform', path: '/super-admin/platforms/create' },
      { id: 'paas-admins', label: 'PAAS Admins', path: '/super-admin/admins' }
    ]},
    { id: 'analytics', label: 'Analytics', icon: '📈', subItems: [
      { id: 'platform-overview', label: 'Platform Overview', path: '/super-admin/dashboard?tab=analytics&sub=platform' },
      { id: 'revenue', label: 'Revenue', path: '/super-admin/dashboard?tab=analytics&sub=revenue' },
      { id: 'usage', label: 'Usage Stats', path: '/super-admin/dashboard?tab=analytics&sub=usage' }
    ]},
    { id: 'packages', label: 'Packages', icon: '📦', subItems: [
      { id: 'all-packages', label: 'All Packages', path: '/super-admin/dashboard?tab=packages' },
      { id: 'add-package', label: 'Add Package', path: '/super-admin/add-package' },
      { id: 'package-usage', label: 'Package Usage', path: '/super-admin/package-usage' }
    ]},
    { id: 'billing', label: 'Payments', icon: '💳' },
    { id: 'income', label: 'Income', icon: '💰' },
    { id: 'admins', label: 'Admins', icon: '👤' },
    { id: 'settings', label: 'Settings', icon: '⚙️' },
  ];

  const [expandedMenus, setExpandedMenus] = useState({});
  const toggleMenu = (id) => {
    setExpandedMenus(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const fetchPAAS = async () => {
    try {
      setLoading(true);
      const res = await api.get('/paas');
      setPaasList(res.data.data || []);
    } catch (error) {
      console.error('Failed to fetch PAAS:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSubscriptions = async () => {
    try {
      const res = await api.get('/billing/subscriptions');
      setSubscriptions(res.data.data || []);
    } catch (error) {
      console.error('Failed to fetch subscriptions:', error);
    }
  };

  const fetchAdmins = async () => {
    try {
      const res = await api.get('/users/admins');
      setAdminsList(res.data.data || []);
    } catch (error) {
      console.error('Failed to fetch admins:', error);
    }
  };

  // Compute filtered PAAS list based on search and status filter
  const filteredPaasList = paasList.filter(paas => {
    const searchLower = paasSearchTerm.toLowerCase();
    const matchesSearch =
      !paasSearchTerm ||
      paas.name?.toLowerCase().includes(searchLower) ||
      paas.domain?.toLowerCase().includes(searchLower) ||
      paas.adminEmail?.toLowerCase().includes(searchLower) ||
      paas.description?.toLowerCase().includes(searchLower);

    const matchesStatus =
      paasStatusFilter === 'all' ||
      (paasStatusFilter === 'active' && paas.isActive) ||
      (paasStatusFilter === 'inactive' && !paas.isActive);

    return matchesSearch && matchesStatus;
  });

  const getLogoUrl = (logoPath) => {
    if (!logoPath) return null;
    if (logoPath.startsWith('http')) return logoPath;
    if (logoPath.startsWith('/assets/') || logoPath.startsWith('/uploads/')) {
      return `/api/v1${logoPath}`;
    }
    return logoPath;
  };

  const handleEditPAAS = (paas) => {
    // Ensure address object is initialized for safe nested updates
    const paasWithAddress = {
      ...paas,
      address: paas.address || { street: '', city: '', state: '', country: '', postalCode: '' },
      subscription: paas.subscription || { plan: 'starter' }
    };
    setEditingPAAS(paasWithAddress);
    setShowEditModal(true);
  };
  const handleStatusToggle = async (paasId, currentStatus) => {
    try {
      await api.patch(`/paas/${paasId}/status`, { isActive: !currentStatus });
      fetchPAAS();
    } catch (error) { console.error('Failed to toggle status:', error); }
  };
  const handleDelete = async (paasId) => {
    if (!window.confirm('Delete this PAAS and all its data?')) return;
    try {
      await api.delete(`/paas/${paasId}`);
      fetchPAAS();
    } catch (error) { console.error('Failed to delete PAAS:', error); }
  };
  const fetchBillingData = async () => {
    try {
      setBillingLoading(true);
      let balRes;
      try {
        balRes = await api.get('/billing/balances');
        if (balRes.data.success && Array.isArray(balRes.data.data)) {
          const allBal = balRes.data.data;
          const total = allBal.reduce((sum, b) => sum + b.balance, 0);
          setBalance({ balance: total, available: total, currency: 'INR', spent: 0, allBalances: allBal });
        }
      } catch (e) {
        balRes = await api.get('/billing/balance');
        if (balRes.data.success) setBalance(balRes.data.data);
      }
      const txRes = await api.get('/billing/transactions');
      const invRes = await api.get('/billing/invoices');
      setTransactions(txRes.data.data.transactions || txRes.data.data || []);
      setInvoices(invRes.data.data.invoices || invRes.data.data || []);
    } catch (error) { console.error('Failed to fetch billing data:', error); }
    finally { setBillingLoading(false); }
  };
  const fetchIncomeData = async () => {
    try {
      const res = await api.get('/billing/income');
      setIncomeData(res.data.data || null);
    } catch (error) { console.error('Failed to fetch income data:', error); }
  };
  const handleDeleteAdmin = async (userId) => {
    if (!window.confirm('Delete this admin?')) return;
    try {
      await api.delete(`/users/admins/${userId}`);
      fetchAdmins();
    } catch (error) { console.error('Failed to delete admin:', error); }
  };
  const openEditSubscription = (sub) => { setEditingSubscription(sub); setShowSubscriptionModal(true); };
  const handleDeleteSubscription = async (packageId) => {
    if (!window.confirm('Delete this subscription?')) return;
    try {
      await api.delete(`/billing/subscriptions/${packageId}`);
      fetchSubscriptions();
    } catch (error) { console.error('Failed to delete subscription:', error); }
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const hasFile = formData.logoFile || formData.faviconFile || formData.androidIconFile || formData.iosIconFile;
      let res;
      if (hasFile) {
        const formDataObj = new FormData();
        const skippedFields = ['logoFilePreview', 'faviconFilePreview', 'androidIconFilePreview', 'iosIconFilePreview', 'logoFile', 'faviconFile', 'androidIconFile', 'iosIconFile'];
        Object.keys(formData).forEach(key => {
          if (!skippedFields.includes(key) && formData[key] !== undefined && formData[key] !== null) {
            formDataObj.append(key, formData[key]);
          }
        });
        if (formData.logoFile) formDataObj.append('logoFile', formData.logoFile);
        if (formData.faviconFile) formDataObj.append('faviconFile', formData.faviconFile);
        if (formData.androidIconFile) formDataObj.append('androidIconFile', formData.androidIconFile);
        if (formData.iosIconFile) formDataObj.append('iosIconFile', formData.iosIconFile);
        res = await api.post('/paas', formDataObj);
      } else {
        res = await api.post('/paas', formData);
      }
      setShowModal(false);
      fetchPAAS();
      alert('PAAS created successfully!');
    } catch (error) {
      alert('Failed to create PAAS: ' + (error.response?.data?.message || error.message));
    }
  };
  const [dragActive, setDragActive] = useState(false);
  const handleDragOver = (e) => { e.preventDefault(); setDragActive(true); };
  const handleDragLeave = (e) => { e.preventDefault(); setDragActive(false); };
  const handleDrop = (e) => {
    e.preventDefault(); setDragActive(false);
    const file = e.dataTransfer.files[0];
    if (file && file.type.startsWith('image/')) {
      setFormData(prev => ({ ...prev, logoFile: file, logoFilePreview: URL.createObjectURL(file) }));
    }
  };
  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prev => ({ ...prev, logoFile: file, logoFilePreview: URL.createObjectURL(file) }));
    }
  };
  const handleCreateAdmin = async (e) => {
    e.preventDefault();
    try {
      const res = await api.post('/users/admins', adminFormData);
      setShowAdminModal(false);
      setAdminFormData({ email: '', password: '', name: '', paasId: '' });
      fetchAdmins();
      alert('Admin created successfully!');
    } catch (error) {
      alert('Failed to create admin: ' + (error.response?.data?.message || error.message));
    }
  };
  const handleUpdateAdmin = async (e) => {
    e.preventDefault();
    if (!editingAdmin) return;

    try {
      const updateData = {
        name: adminFormData.name
      };
      // Only include password if provided (not empty)
      if (adminFormData.password && adminFormData.password.trim() !== '') {
        updateData.password = adminFormData.password;
      }

      await api.put(`/users/admins/${editingAdmin.userId}`, updateData);
      setShowAdminModal(false);
      setEditingAdmin(null);
      setAdminFormData({ email: '', password: '', name: '', paasId: '' });
      fetchAdmins();
      alert('Admin updated successfully!');
    } catch (error) {
      alert('Failed to update admin: ' + (error.response?.data?.message || error.message));
    }
  };
  const handleAdminStatusToggle = async (userId, currentStatus) => {
    try {
      await api.patch(`/users/admins/${userId}`, { isActive: !currentStatus });
      fetchAdmins();
    } catch (error) { console.error('Failed to toggle admin status:', error); }
  };
  const handleSubscriptionSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingSubscription) {
        await api.put(`/billing/subscriptions/${editingSubscription.packageId}`, formData);
      }
      setShowSubscriptionModal(false);
      fetchSubscriptions();
      alert('Subscription saved successfully!');
    } catch (error) {
      alert('Failed to save subscription: ' + (error.response?.data?.message || error.message));
    }
  };
  const handleUpdatePAAS = async (e) => {
    e.preventDefault();
    try {
      // Build update payload from editingPAAS
      const updateData = {
        name: editingPAAS.name,
        companyName: editingPAAS.companyName,
        contactEmail: editingPAAS.contactEmail,
        supportEmail: editingPAAS.supportEmail,
        description: editingPAAS.description,
        primaryColor: editingPAAS.primaryColor,
        domain: editingPAAS.domain,
        termsUrl: editingPAAS.termsUrl,
        privacyUrl: editingPAAS.privacyUrl,
        address: editingPAAS.address,
        plan: editingPAAS.subscription?.plan || editingPAAS.plan,
        maxSaaS: editingPAAS.maxSaaS,
        maxTenants: editingPAAS.maxTenants,
        apiRateLimit: editingPAAS.apiRateLimit
      };

      // Handle file upload if new logo selected
      if (editingPAAS.logoFile) {
        const formDataObj = new FormData();
        Object.keys(updateData).forEach(key => {
          if (updateData[key] !== undefined && updateData[key] !== null) {
            formDataObj.append(key, updateData[key]);
          }
        });
        formDataObj.append('logoFile', editingPAAS.logoFile);
        await api.put(`/paas/${editingPAAS.paasId}`, formDataObj);
      } else {
        await api.put(`/paas/${editingPAAS.paasId}`, updateData);
      }

      setShowEditModal(false);
      fetchPAAS();
      alert('PAAS updated successfully!');
    } catch (error) {
      alert('Failed to update PAAS: ' + (error.response?.data?.message || error.message));
    }
  };
  const handleTopup = async () => {
    try {
      await api.post('/billing/topup', { amount: topupAmount });
      setShowTopupModal(false);
      fetchBillingData();
      alert('Top-up successful!');
    } catch (error) {
      alert('Failed to top-up: ' + (error.response?.data?.message || error.message));
    }
  };

  useEffect(() => {
    const path = location.pathname;
    const subTab = searchParams.get('sub');
    const tabParam = searchParams.get('tab');
    const actionParam = searchParams.get('action');

    if (path.includes('/platforms/create')) {
      setShowModal(true);
    } else if (path.includes('/admins')) {
      setActiveTab('admins');
      fetchAdmins();
    } else if (path.includes('/platforms')) {
      setActiveTab('paas');
    } else if (path.includes('/add-package') || path.includes('/package-usage')) {
      setActiveTab('packages');
    } else if (tabParam) {
      setActiveTab(tabParam);
    }

    if (actionParam === 'topup') {
      setShowTopupModal(true);
    }

    if (tabParam === 'settings' && subTab) {
      setSettingsSubTab(subTab);
    } else if (tabParam === 'settings') {
      setSettingsSubTab('general');
    }

    fetchPAAS();
    fetchSubscriptions();

    // Fetch billing and income data for overview charts
    if (path === '/super-admin/dashboard' || path === '/super-admin') {
      fetchBillingData();
      fetchIncomeData();
    }

    // Auto-expand menus based on current path
    const currentTab = searchParams.get('tab');
    if (location.pathname.includes('/platforms') || location.pathname.includes('/admins')) {
      setExpandedMenus(prev => ({ ...prev, paas: true }));
    }
    if (currentTab === 'packages' || location.pathname.includes('/add-package') || location.pathname.includes('/package-usage')) {
      setExpandedMenus(prev => ({ ...prev, packages: true }));
    }
    if (currentTab === 'analytics' || location.pathname.includes('/dashboard?tab=analytics')) {
      setExpandedMenus(prev => ({ ...prev, analytics: true }));
    }
  }, [location.pathname, searchParams]);

  // Polling for real-time data updates
  useEffect(() => {
    const pollPAAS = setInterval(() => {
      fetchPAAS();
    }, 10000); // 10 seconds

    const pollBilling = setInterval(() => {
      fetchBillingData();
      fetchIncomeData();
    }, 15000); // 15 seconds

    const pollAdmins = setInterval(() => {
      if (activeTab === 'admins') fetchAdmins();
    }, 15000); // 15 seconds

    const pollSubscriptions = setInterval(() => {
      fetchSubscriptions();
    }, 30000); // 30 seconds

    return () => {
      clearInterval(pollPAAS);
      clearInterval(pollBilling);
      clearInterval(pollAdmins);
      clearInterval(pollSubscriptions);
    };
  }, [activeTab]);

  return (
    <div className="flex h-screen bg-gray-100">
      {/* Left Sidebar */}
      <div className="w-64 bg-white shadow-lg flex flex-col">
        <div className="p-4 border-b">
          <h2 className="text-xl font-bold text-blue-600">Super Admin</h2>
          <p className="text-sm text-gray-500">Platform Dashboard</p>
        </div>
        <nav className="flex-1 p-4 overflow-y-auto">
          {menuItems.map((item) => (
            <div key={item.id} className="mb-2">
              {item.subItems ? (
                <>
                  <button
                    onClick={() => {
                      toggleMenu(item.id);
                      if (item.id === 'paas') navigate('/super-admin/platforms');
                      else if (item.id === 'analytics') navigate('/super-admin/dashboard?tab=analytics&sub=platform');
                      else if (item.id === 'packages') navigate('/super-admin/dashboard?tab=packages');
                      else if (item.id === 'settings') navigate('/super-admin/dashboard?tab=settings');
                    }}
                    className={`w-full text-left px-4 py-3 rounded-lg flex items-center justify-between ${
                      activeTab === item.id || item.subItems.some(sub => {
                        const subPathBase = sub.path.split('?')[0];
                        const currentPath = location.pathname;
                        const currentTab = searchParams.get('tab');
                        const currentSub = searchParams.get('sub');
                        return currentPath === subPathBase || 
                          (currentTab === item.id && (!currentSub || sub.path.includes(`sub=${currentSub}`)));
                      })
                        ? 'bg-blue-50 text-blue-700 border-l-4 border-blue-600'
                        : 'text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    <span className="flex items-center gap-3">
                      <span>{item.icon}</span>
                      <span className="font-medium">{item.label}</span>
                    </span>
                    <svg className={`w-4 h-4 transition-transform ${expandedMenus[item.id] ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {expandedMenus[item.id] && (
                    <div className="ml-8 mt-1 space-y-1">
                      {item.subItems.map((sub) => (
                        <button
                          key={sub.id}
                          onClick={() => {
                            navigate(sub.path);
                          }}
                          className={`w-full text-left px-4 py-2 rounded-lg text-sm ${
                            (location.pathname === sub.path.split('?')[0] && (searchParams.toString() === sub.path.split('?')[1] || !sub.path.includes('?'))) ||
                            (sub.path.includes('?') && location.pathname + '?' + searchParams.toString() === sub.path)
                              ? 'bg-blue-100 text-blue-700'
                              : 'text-gray-600 hover:bg-gray-50'
                          }`}
                        >
                          {sub.label}
                        </button>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <button
                  onClick={() => {
                    setActiveTab(item.id);
                    if (item.id === 'packages') navigate('/super-admin/dashboard?tab=packages');
                    else if (item.id === 'billing') navigate('/super-admin/dashboard?tab=billing');
                    else if (item.id === 'income') navigate('/super-admin/dashboard?tab=income');
                    else if (item.id === 'settings') navigate('/super-admin/dashboard?tab=settings&sub=general');
                    else if (item.id === 'admins') navigate('/super-admin/admins');
                    else navigate('/super-admin/dashboard');
                  }}
                  className={`w-full text-left px-4 py-3 rounded-lg flex items-center gap-3 ${
                    activeTab === item.id
                      ? 'bg-blue-50 text-blue-700 border-l-4 border-blue-600'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <span>{item.icon}</span>
                  <span className="font-medium">{item.label}</span>
                </button>
              )}
            </div>
          ))}
        </nav>
        <div className="p-4 border-t" >
          <button
            onClick={handleLogout}
            className="w-full text-left px-4 py-2 text-red-600 hover:bg-red-50 rounded flex items-center gap-2"
          >
            <span>🚪</span>
            <span>Logout</span>
          </button>
        </div>
      </div>

       {/* Main Content */}
       <div className="flex-1 flex flex-col overflow-hidden">
         {/* Top Header */}
         <header className="bg-white shadow-sm border-b px-6 py-4 flex justify-between items-center">
           <div>
             <h1 className="text-2xl font-bold text-gray-800">
               {menuItems.find(m => m.id === activeTab)?.label || 'Dashboard'}
             </h1>
           </div>
          <div className="flex items-center gap-4">
              {activeTab === 'overview' && (
                <button
                  onClick={() => setShowModal(true)}
                  className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                >
                  + Create Platform
                </button>
              )}
               {activeTab === 'billing' && (
                 <div className="flex gap-2">
                   <button
                     onClick={() => { fetchBillingData(); fetchIncomeData(); }}
                     className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                   >
                     Refresh Data
                   </button>
                   <button
                     onClick={() => setShowTransferModal(true)}
                     className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
                   >
                     Transfers
                   </button>
                 </div>
               )}
               <button onClick={handleLogout} className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700">
                 Logout
               </button>
            </div>
         </header>

         {/* Scrollable Content */}
         <div className="flex-1 overflow-y-auto p-6">

        {activeTab === 'overview' && (
        <>
          <StatsGrid>
            <StatCard title="Total Platforms" value={paasList.length} />
            <StatCard title="Active Platforms" value={paasList.filter(p => p.isActive).length} change={12} />
            <StatCard title="Total Systems" value={paasList.reduce((sum, p) => sum + (p.analytics?.totalSaaS || 0),0)} change={8} />
            <StatCard title="Total Clients" value={paasList.reduce((sum, p) => sum + (p.analytics?.totalTenants || 0),0)} />
          </StatsGrid>

          <AnalyticsGrid>
            <LineChartWidget 
              title="Platform Growth" 
              data={generateMockData(14)} 
              xKey="date" 
              yKeys={['users', 'apiCalls']} 
            />
            <PieChartWidget 
              title="Platform Distribution" 
              data={[
                { name: 'Active', value: paasList.filter(p => p.isActive).length },
                { name: 'Inactive', value: paasList.filter(p => !p.isActive).length }
              ]} 
              nameKey="name" 
              valueKey="value" 
            />
          </AnalyticsGrid>

          {/* Packages, Payments, Income Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
            <div className="bg-white rounded shadow p-4">
              <h3 className="text-lg font-semibold mb-4">Package Subscriptions</h3>
              {subscriptions.length > 0 ? (
                <PieChartWidget 
                  data={(() => {
                    const types = {};
                    subscriptions.forEach(sub => {
                      const type = sub.packageType || 'other';
                      types[type] = (types[type] || 0) + 1;
                    });
                    return Object.entries(types).map(([name, value]) => ({ name, value }));
                  })()} 
                  nameKey="name" 
                  valueKey="value" 
                />
              ) : (
                <p className="text-gray-500 text-center py-8">No subscription data</p>
              )}
            </div>

            <div className="bg-white rounded shadow p-4">
              <h3 className="text-lg font-semibold mb-4">Payment Trends</h3>
              {transactions.length > 0 ? (
                <LineChartWidget 
                  title=""
                  data={(() => {
                    const last7 = transactions.slice(-7).reverse();
                    return last7.map(tx => ({
                      date: new Date(tx.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
                      amount: tx.amount || 0
                    }));
                  })()} 
                  xKey="date" 
                  yKeys={['amount']} 
                />
              ) : (
                <p className="text-gray-500 text-center py-8">No transaction data</p>
              )}
            </div>

            <div className="bg-white rounded shadow p-4">
              <h3 className="text-lg font-semibold mb-4">Income Summary</h3>
              {incomeData ? (
                <div>
                  <div className="mb-4">
                    <div className="text-3xl font-bold text-green-700">{incomeData.total || 0} {incomeData.currency || 'INR'}</div>
                    <div className="text-sm text-gray-500">Total Income (Bottom-up)</div>
                  </div>
                  {incomeData.byPeriod && incomeData.byPeriod.length > 0 ? (
                    <BarChartWidget 
                      data={incomeData.byPeriod.map(p => ({
                        period: p.period || '',
                        income: p.income || 0
                      }))} 
                      xKey="period" 
                      yKey="income" 
                    />
                  ) : null}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-8">No income data</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            <div className="bg-white rounded shadow p-4">
              <h2 className="text-lg font-semibold mb-4">Platform Activity</h2>
              <ActivityLogs limit={15} />
            </div>
          </div>
        </>
      )}

      {activeTab === 'paas' && (
        <div className="bg-white rounded shadow overflow-hidden">
          {/* Search and Filter Bar */}
          <div className="p-4 border-b flex flex-wrap gap-4 items-center">
            <div className="flex-1 min-w-[200px]">
              <input
                type="text"
                placeholder="Search platforms by name, domain, or admin..."
                value={paasSearchTerm}
                onChange={(e) => setPaasSearchTerm(e.target.value)}
                className="w-full px-4 py-2 border rounded-lg"
              />
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-600">Status:</label>
              <select
                value={paasStatusFilter}
                onChange={(e) => setPaasStatusFilter(e.target.value)}
                className="px-4 py-2 border rounded-lg"
              >
                <option value="all">All</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
            <div className="text-sm text-gray-500">
              Showing {filteredPaasList.length} of {paasList.length} platforms
            </div>
          </div>

          <table className="min-w-full">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-4 py-2 text-left w-12">#</th>
                <th className="px-4 py-2 text-left">Logo</th>
                <th className="px-4 py-2 text-left">Name</th>
                <th className="px-4 py-2 text-left">Platform Admin</th>
                <th className="px-4 py-2 text-left">Domain</th>
                <th className="px-4 py-2 text-left">Plan</th>
                <th className="px-4 py-2 text-left">Status</th>
                <th className="px-4 py-2 text-left">Created</th>
                <th className="px-4 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="9" className="px-4 py-4 text-center">Loading...</td></tr>
              ) : filteredPaasList.length === 0 ? (
                <tr><td colSpan="9" className="px-4 py-4 text-center">
                  {paasList.length === 0
                    ? 'No platforms found. Create one to get started.'
                    : 'No platforms match your search criteria.'}
                </td></tr>
              ) : (
                filteredPaasList.map((paas, index) => (
                  <tr key={paas.paasId} className="border-t hover:bg-gray-50">
                    <td className="px-4 py-3 text-gray-500 text-sm">{index + 1}</td>
                    <td className="px-4 py-3">
                      {paas.logo ? (
                        <img
                          src={getLogoUrl(paas.logo)}
                          alt={paas.name}
                          className="w-10 h-10 rounded object-cover border"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                      ) : null}
                      <div
                        className={`w-10 h-10 rounded bg-gray-200 flex items-center justify-center text-gray-600 font-bold ${paas.logo ? 'hidden' : 'flex'}`}
                      >
                        {paas.name?.charAt(0)?.toUpperCase() || '?'}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-medium">{paas.name}</div>
                      <div className="text-xs text-gray-400">ID: {paas.paasId?.slice(0, 8)}...</div>
                      <div className="text-sm text-gray-500">{paas.description?.slice(0, 50) || '-'}{paas.description?.length > 50 ? '...' : ''}</div>
                    </td>
                    <td className="px-4 py-3">
                      {paas.adminEmail || '-'}
                    </td>
                    <td className="px-4 py-3">
                      {paas.domain ? (
                        <a
                          href={`https://${paas.domain}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:underline"
                        >
                          {paas.domain}
                        </a>
                      ) : '-'}
                    </td>
                    <td className="px-4 py-3">
                      <span className="px-2 py-1 rounded text-sm bg-blue-100 text-blue-800 capitalize">
                        {paas.subscription?.plan || 'free'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded text-sm ${paas.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        {paas.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3">{new Date(paas.createdAt).toLocaleDateString()}</td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => handleEditPAAS(paas)} className="text-green-600 hover:underline mr-3">Edit</button>
                      <button onClick={() => navigate(`/paas-admin/${paas.paasId}`)} className="text-blue-600 hover:underline mr-3">Manage</button>
                      <button onClick={() => handleStatusToggle(paas.paasId, paas.isActive)} className="text-yellow-600 hover:underline mr-3">
                        {paas.isActive ? 'Deactivate' : 'Activate'}
                      </button>
                      <button onClick={() => handleDelete(paas.paasId)} className="text-red-600 hover:underline">Delete</button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {activeTab === 'packages' && (
        <PackageManager level="super" />
      )}

       {activeTab === 'billing' && (
         <div className="p-6">
           <div className="bg-white rounded shadow p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold">Platform Revenue</h2>
                <div className="flex gap-2">
                  <button
                    onClick={() => { fetchBillingData(); fetchIncomeData(); }}
                    className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                  >
                    Refresh Data
                  </button>
                  <button
                    onClick={() => setShowTransferModal(true)}
                    className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
                  >
                    Transfers
                  </button>
                </div>
              </div>
             {billingLoading ? (
               <div className="text-center py-8">Loading billing data...</div>
             ) : (
               <>
<div className="grid grid-cols-4 gap-6 mb-6">
                  <div className="p-4 bg-green-50 rounded border border-green-200">
                    <div className="text-green-600 text-sm">Total Balance</div>
                    <div className="text-3xl font-bold text-green-700">₹{balance?.balance?.toLocaleString() || 0}</div>
                  </div>
                  <div className="p-4 bg-blue-50 rounded border border-blue-200">
                    <div className="text-blue-600 text-sm">Available Credits</div>
                    <div className="text-3xl font-bold text-blue-700">₹{balance?.available?.toLocaleString() || 0}</div>
                  </div>
                  <div className="p-4 bg-purple-50 rounded border border-purple-200">
                    <div className="text-purple-600 text-sm">Total Spent</div>
                    <div className="text-3xl font-bold text-purple-700">₹{balance?.spent?.toLocaleString() || 0}</div>
                  </div>
                  <div className="p-4 bg-yellow-50 rounded border border-yellow-200">
                    <div className="text-yellow-600 text-sm">Income (Bottom-up)</div>
                    <div className="text-3xl font-bold text-yellow-700">₹{incomeData?.total?.toLocaleString() || 0}</div>
                  </div>
                </div>

                {balance?.allBalances && balance.allBalances.length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold mb-3">All Organization Balances</h3>
                    <div className="bg-white rounded border overflow-hidden">
                      <table className="min-w-full">
                        <thead className="bg-gray-100">
                          <tr>
                            <th className="px-4 py-2 text-left">Organization</th>
                            <th className="px-4 py-2 text-right">Balance</th>
                            <th className="px-4 py-2 text-right">Currency</th>
                          </tr>
                        </thead>
                        <tbody>
                          {balance.allBalances.map((b, i) => (
                            <tr key={i} className="border-t">
                              <td className="px-4 py-2 font-mono text-sm">{b.tenantId?.slice(0,8)}...</td>
                              <td className="px-4 py-2 text-right font-medium">₹{b.balance?.toLocaleString()}</td>
                              <td className="px-4 py-2 text-right">{b.currency}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                 <div className="mb-6">
                   <h3 className="text-lg font-semibold mb-3">Recent Transactions</h3>
                   {transactions.length === 0 ? (
                     <p className="text-gray-500">No transactions yet.</p>
                   ) : (
                     <div className="bg-gray-50 rounded overflow-hidden">
                       <table className="min-w-full">
                         <thead className="bg-gray-100">
                           <tr>
                             <th className="px-4 py-2 text-left">Type</th>
                             <th className="px-4 py-2 text-left">Amount</th>
                             <th className="px-4 py-2 text-left">Description</th>
                             <th className="px-4 py-2 text-left">Date</th>
                           </tr>
                         </thead>
                         <tbody>
                           {transactions.map((tx) => (
                             <tr key={tx._id} className="border-t">
                               <td className="px-4 py-2">
                                 <span className={`px-2 py-1 rounded text-xs ${tx.type === 'credit' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                   {tx.type}
                                 </span>
                               </td>
                               <td className="px-4 py-2 font-medium">{tx.amount} {balance?.currency || 'INR'}</td>
                               <td className="px-4 py-2 text-sm">{tx.description || '-'}</td>
                               <td className="px-4 py-2 text-sm">{new Date(tx.createdAt).toLocaleDateString()}</td>
                             </tr>
                           ))}
                         </tbody>
                       </table>
                     </div>
                   )}
                 </div>
               </>
             )}
           </div>
         </div>
        )}

       {activeTab === 'income' && (
         <div className="p-6">
           <div className="bg-white rounded shadow p-6 mb-6">
             <div className="flex justify-between items-center mb-4">
               <h2 className="text-xl font-bold">Income Tracking (Bottom-up)</h2>
               <button
                 onClick={fetchIncomeData}
                 className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
               >
                 Refresh Income Data
               </button>
             </div>
             {incomeData ? (
               <div className="grid grid-cols-3 gap-6 mb-6">
                 <div className="p-4 bg-green-50 rounded border border-green-200">
                   <div className="text-green-600 text-sm">Total Income</div>
                   <div className="text-3xl font-bold text-green-700">{incomeData.total || 0} {incomeData.currency || 'INR'}</div>
                   <div className="text-green-600 text-sm">From all PAAS/SaaS</div>
                 </div>
                 <div className="p-4 bg-blue-50 rounded border border-blue-200">
                   <div className="text-blue-600 text-sm">Currency</div>
                   <div className="text-3xl font-bold text-blue-700">{incomeData.currency || 'INR'}</div>
                 </div>
               </div>
             ) : (
               <div className="text-center py-8">Click "Refresh Income Data" to load.</div>
             )}
           </div>
         </div>
       )}

       {activeTab === 'admins' && (
        <div className="bg-white rounded shadow overflow-hidden">
          <div className="flex justify-between items-center p-4 border-b">
            <h2 className="text-lg font-semibold">Platform Admins</h2>
            <button
              onClick={() => { setEditingAdmin(null); setAdminFormData({ email: '', password: '', name: '', paasId: '' }); setShowAdminModal(true); }}
              className="bg-blue-600 text-white px-3 py-1.5 rounded hover:bg-blue-700 text-sm"
            >
              + Create Admin
            </button>
          </div>
          <table className="min-w-full">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-4 py-2 text-left">Name</th>
                <th className="px-4 py-2 text-left">Email</th>
                <th className="px-4 py-2 text-left">Role</th>
                <th className="px-4 py-2 text-left">Status</th>
                <th className="px-4 py-2 text-left">Created</th>
                <th className="px-4 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {adminsList.length === 0 ? (
                <tr><td colSpan="6" className="px-4 py-4 text-center">No admins found.</td></tr>
              ) : (
                adminsList.map((admin) => (
                  <tr key={admin._id} className="border-t">
                    <td className="px-4 py-3">{admin.name}</td>
                    <td className="px-4 py-3">{admin.email}</td>
                    <td className="px-4 py-3">
                      <span className="px-2 py-1 rounded text-sm bg-blue-100 text-blue-800">{admin.role}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded text-sm ${admin.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        {admin.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3">{new Date(admin.createdAt).toLocaleDateString()}</td>
                    <td className="px-4 py-3 text-right">
                      <button 
                        onClick={() => { 
                          setEditingAdmin(admin); 
                          setAdminFormData({ 
                            name: admin.name || '', 
                            email: admin.email || '', 
                            password: '' 
                          }); 
                          setShowAdminModal(true); 
                        }} 
                        className="text-blue-600 hover:underline mr-3"
                      >
                        Edit
                      </button>
                      <button onClick={() => handleDeleteAdmin(admin.userId)} className="text-red-600 hover:underline">Delete</button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {activeTab === 'analytics' && (
        <div className="space-y-6">
          {/* Analytics Overview */}
          <div className="bg-white rounded shadow p-6">
            <h2 className="text-xl font-bold mb-4">Platform Analytics</h2>
            <div className="flex border-b mb-4">
              {[
                { key: 'platform', label: 'Platform Overview' },
                { key: 'revenue', label: 'Revenue' },
                { key: 'usage', label: 'Usage Stats' }
              ].map(sub => (
                <button
                  key={sub.key}
                  onClick={() => {
                    setSettingsSubTab(sub.key);
                    const newUrl = new URL(window.location.href);
                    newUrl.searchParams.set('tab', 'analytics');
                    newUrl.searchParams.set('sub', sub.key);
                    navigate(newUrl.pathname + newUrl.search);
                  }}
                  className={`px-4 py-2 ${settingsSubTab === sub.key ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-500'}`}
                >
                  {sub.label}
                </button>
              ))}
            </div>

            {settingsSubTab === 'platform' && (
              <div className="space-y-4">
                <div className="grid grid-cols-4 gap-4">
                  <div className="p-4 bg-blue-50 rounded border border-blue-200">
                    <div className="text-blue-600 text-sm">Total Platforms</div>
                    <div className="text-2xl font-bold text-blue-700">{paasList.length}</div>
                  </div>
                  <div className="p-4 bg-green-50 rounded border border-green-200">
                    <div className="text-green-600 text-sm">Active Platforms</div>
                    <div className="text-2xl font-bold text-green-700">{paasList.filter(p => p.isActive).length}</div>
                  </div>
                  <div className="p-4 bg-purple-50 rounded border border-purple-200">
                    <div className="text-purple-600 text-sm">Total SaaS</div>
                    <div className="text-2xl font-bold text-purple-700">{paasList.reduce((sum, p) => sum + (p.analytics?.totalSaaS || 0), 0)}</div>
                  </div>
                  <div className="p-4 bg-yellow-50 rounded border border-yellow-200">
                    <div className="text-yellow-600 text-sm">Total Tenants</div>
                    <div className="text-2xl font-bold text-yellow-700">{paasList.reduce((sum, p) => sum + (p.analytics?.totalTenants || 0), 0)}</div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-6">
                  <div className="border rounded p-4">
                    <h3 className="font-semibold mb-3">Platform Growth</h3>
                    <LineChartWidget
                      title=""
                      data={generateMockData(14)}
                      xKey="date"
                      yKeys={['users', 'apiCalls']}
                    />
                  </div>
                  <div className="border rounded p-4">
                    <h3 className="font-semibold mb-3">Platform Distribution</h3>
                    <PieChartWidget
                      data={[
                        { name: 'Active', value: paasList.filter(p => p.isActive).length },
                        { name: 'Inactive', value: paasList.filter(p => !p.isActive).length }
                      ]}
                      nameKey="name"
                      valueKey="value"
                    />
                  </div>
                </div>
              </div>
            )}

            {settingsSubTab === 'revenue' && (
              <div className="space-y-4">
                <div className="grid grid-cols-3 gap-4">
                  <div className="p-4 bg-green-50 rounded border border-green-200">
                    <div className="text-green-600 text-sm">Total Revenue</div>
                    <div className="text-2xl font-bold text-green-700">₹{incomeData?.total?.toLocaleString() || 0}</div>
                  </div>
                  <div className="p-4 bg-blue-50 rounded border border-blue-200">
                    <div className="text-blue-600 text-sm">This Month</div>
                    <div className="text-2xl font-bold text-blue-700">₹{incomeData?.byPeriod?.slice(-1)[0]?.income?.toLocaleString() || 0}</div>
                  </div>
                  <div className="p-4 bg-purple-50 rounded border border-purple-200">
                    <div className="text-purple-600 text-sm">PAAS Count</div>
                    <div className="text-2xl font-bold text-purple-700">{incomeData?.paasCount || 0}</div>
                  </div>
                </div>

                {incomeData?.byPeriod && incomeData.byPeriod.length > 0 && (
                  <div className="border rounded p-4">
                    <h3 className="font-semibold mb-3">Revenue by Month</h3>
                    <BarChartWidget
                      data={incomeData.byPeriod.map(p => ({
                        period: p.period,
                        income: p.income
                      }))}
                      xKey="period"
                      yKey="income"
                    />
                  </div>
                )}
              </div>
            )}

            {settingsSubTab === 'usage' && (
              <div className="space-y-4">
                <div className="grid grid-cols-4 gap-4">
                  <div className="p-4 bg-blue-50 rounded border border-blue-200">
                    <div className="text-blue-600 text-sm">Total API Calls</div>
                    <div className="text-2xl font-bold text-blue-700">{(1234567).toLocaleString()}</div>
                  </div>
                  <div className="p-4 bg-green-50 rounded border border-green-200">
                    <div className="text-green-600 text-sm">Active Users</div>
                    <div className="text-2xl font-bold text-green-700">{(paasList.reduce((sum, p) => sum + (p.analytics?.totalTenants || 0) * 5, 0)).toLocaleString()}</div>
                  </div>
                  <div className="p-4 bg-yellow-50 rounded border border-yellow-200">
                    <div className="text-yellow-600 text-sm">Avg Usage/Platform</div>
                    <div className="text-2xl font-bold text-yellow-700">{(987654).toLocaleString()}</div>
                  </div>
                  <div className="p-4 bg-purple-50 rounded border border-purple-200">
                    <div className="text-purple-600 text-sm">Peak Time</div>
                    <div className="text-2xl font-bold text-purple-700">2:00 PM</div>
                  </div>
                </div>

                <div className="border rounded p-4">
                  <h3 className="font-semibold mb-3">API Calls Over Time</h3>
                  <LineChartWidget
                    title=""
                    data={generateMockData(30)}
                    xKey="date"
                    yKeys={['apiCalls']}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'settings' && (
        <div className="bg-white rounded shadow p-6">
          <h2 className="text-lg font-semibold mb-4">Platform Settings</h2>
          <div className="flex border-b mb-4">
            {[
              { key: 'general', label: 'General' },
              { key: 'subscriptions', label: 'Subscriptions' }
            ].map(sub => (
              <button
                key={sub.key}
                onClick={() => {
                  setSettingsSubTab(sub.key);
                  navigate(`/super-admin/dashboard?tab=settings&sub=${sub.key}`);
                }}
                className={`px-4 py-2 ${settingsSubTab === sub.key ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-500'}`}
              >
                {sub.label}
              </button>
            ))}
          </div>

          {settingsSubTab === 'general' && (
            <p className="text-gray-500">Configure global platform settings.</p>
          )}

          {settingsSubTab === 'subscriptions' && (
            <div>
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">Subscription Plans</h3>
                <button
                  onClick={() => { setEditingSubscription(null); setShowSubscriptionModal(true); }}
                  className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                >
                  + Create Subscription
                </button>
              </div>

              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-gray-100 border-b">
                    <th className="text-left p-3">Name</th>
                    <th className="text-left p-3">Type</th>
                    <th className="text-left p-3">Monthly</th>
                    <th className="text-left p-3">Yearly</th>
                    <th className="text-left p-3">Users</th>
                    <th className="text-left p-3">Status</th>
                    <th className="text-left p-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {subscriptions.map(sub => (
                    <tr key={sub.packageId} className="border-b hover:bg-gray-50">
                      <td className="p-3">{sub.name}</td>
                      <td className="p-3">{sub.type}</td>
                      <td className="p-3">${sub.pricing?.monthlyPrice || 0}</td>
                      <td className="p-3">${sub.pricing?.yearlyPrice || 0}</td>
                      <td className="p-3">{sub.limits?.users || 1}</td>
                      <td className="p-3">
                        <span className={`px-2 py-1 rounded text-sm ${sub.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                          {sub.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="p-3">
                        <button onClick={() => openEditSubscription(sub)} className="text-blue-600 hover:underline mr-3">Edit</button>
                        <button onClick={() => handleDeleteSubscription(sub.packageId)} className="text-red-600 hover:underline">Delete</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {subscriptions.length === 0 && (
                <p className="text-gray-500 text-center py-4">No subscriptions yet. Create one to get started.</p>
              )}
                  </div>
                )}
              </div>
            )}
          
        {showTransferModal && (
          <CreditTransferModal
            isOpen={showTransferModal}
            onClose={() => setShowTransferModal(false)}
            mode="transfer"
            userRole="superadmin"
            userContext={{}}
          />
        )}

      {/* Create Platform Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto py-6">
          <div className="bg-white p-6 rounded-lg w-full max-w-4xl my-6 max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">Create New Platform (White Label)</h2>
            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-2 gap-4">
                {/* Basic Info */}
                <div className="col-span-2">
                  <h3 className="text-lg font-semibold mb-2 border-b pb-1">Basic Information</h3>
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-1">Platform Name *</label>
                  <input type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="w-full border rounded px-3 py-2" required />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-1">Company Name</label>
                  <input type="text" value={formData.companyName} onChange={(e) => setFormData({ ...formData, companyName: e.target.value })} className="w-full border rounded px-3 py-2" />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-1">Contact Email *</label>
                  <input type="email" value={formData.contactEmail} onChange={(e) => setFormData({ ...formData, contactEmail: e.target.value })} className="w-full border rounded px-3 py-2" required />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-1">Support Email</label>
                  <input type="email" value={formData.supportEmail} onChange={(e) => setFormData({ ...formData, supportEmail: e.target.value })} className="w-full border rounded px-3 py-2" />
                </div>
                <div className="mb-4 col-span-2">
                  <label className="block text-sm font-medium mb-1">Description</label>
                  <textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} className="w-full border rounded px-3 py-2" rows="2" />
                </div>

                {/* Branding */}
                <div className="col-span-2">
                  <h3 className="text-lg font-semibold mb-2 border-b pb-1">Branding & Logo</h3>
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-1">Primary Color</label>
                  <input type="color" value={formData.primaryColor} onChange={(e) => setFormData({ ...formData, primaryColor: e.target.value })} className="w-full h-10 border rounded" />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-1">Domain</label>
                  <input type="text" value={formData.domain} onChange={(e) => setFormData({ ...formData, domain: e.target.value })} className="w-full border rounded px-3 py-2" placeholder="platform.example.com" />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-1">Logo (Web)</label>
                  <div
                    className={`border-2 border-dashed rounded p-4 text-center cursor-pointer transition-colors ${dragActive.logoFile ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-gray-400'}`}
                    onDragOver={(e) => handleDragOver(e, 'logoFile')}
                    onDragLeave={(e) => handleDragLeave(e, 'logoFile')}
                    onDrop={(e) => handleDrop(e, 'logoFile')}
                    onClick={() => document.getElementById('logoFileInput').click()}
                  >
                    <input type="file" id="logoFileInput" accept="image/png,image/jpeg,image/svg+xml" onChange={(e) => handleLogoUpload(e, 'logoFile')} className="hidden" />
                    {formData.logoFilePreview ? (
                      <img src={formData.logoFilePreview} alt="Logo preview" className="h-16 object-contain mx-auto" />
                    ) : (
                      <p className="text-sm text-gray-500">Drag & drop or click to upload</p>
                    )}
                  </div>
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-1">Favicon</label>
                  <div
                    className={`border-2 border-dashed rounded p-4 text-center cursor-pointer transition-colors ${dragActive.faviconFile ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-gray-400'}`}
                    onDragOver={(e) => handleDragOver(e, 'faviconFile')}
                    onDragLeave={(e) => handleDragLeave(e, 'faviconFile')}
                    onDrop={(e) => handleDrop(e, 'faviconFile')}
                    onClick={() => document.getElementById('faviconFileInput').click()}
                  >
                    <input type="file" id="faviconFileInput" accept="image/png,image/x-icon" onChange={(e) => handleLogoUpload(e, 'faviconFile')} className="hidden" />
                    {formData.faviconFilePreview ? (
                      <img src={formData.faviconFilePreview} alt="Favicon preview" className="h-8 w-8 mx-auto" />
                    ) : (
                      <p className="text-sm text-gray-500">Drag & drop or click to upload</p>
                    )}
                  </div>
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-1">Android Icon</label>
                  <div
                    className={`border-2 border-dashed rounded p-4 text-center cursor-pointer transition-colors ${dragActive.androidIconFile ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-gray-400'}`}
                    onDragOver={(e) => handleDragOver(e, 'androidIconFile')}
                    onDragLeave={(e) => handleDragLeave(e, 'androidIconFile')}
                    onDrop={(e) => handleDrop(e, 'androidIconFile')}
                    onClick={() => document.getElementById('androidIconFileInput').click()}
                  >
                    <input type="file" id="androidIconFileInput" accept="image/png" onChange={(e) => handleLogoUpload(e, 'androidIconFile')} className="hidden" />
                    {formData.androidIconFilePreview ? (
                      <img src={formData.androidIconFilePreview} alt="Android preview" className="h-12 mx-auto" />
                    ) : (
                      <p className="text-sm text-gray-500">Drag & drop or click to upload</p>
                    )}
                  </div>
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-1">iOS Icon</label>
                  <div
                    className={`border-2 border-dashed rounded p-4 text-center cursor-pointer transition-colors ${dragActive.iosIconFile ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-gray-400'}`}
                    onDragOver={(e) => handleDragOver(e, 'iosIconFile')}
                    onDragLeave={(e) => handleDragLeave(e, 'iosIconFile')}
                    onDrop={(e) => handleDrop(e, 'iosIconFile')}
                    onClick={() => document.getElementById('iosIconFileInput').click()}
                  >
                    <input type="file" id="iosIconFileInput" accept="image/png" onChange={(e) => handleLogoUpload(e, 'iosIconFile')} className="hidden" />
                    {formData.iosIconFilePreview ? (
                      <img src={formData.iosIconFilePreview} alt="iOS preview" className="h-12 mx-auto" />
                    ) : (
                      <p className="text-sm text-gray-500">Drag & drop or click to upload</p>
                    )}
                  </div>
                </div>

                {/* Legal */}
                <div className="col-span-2">
                  <h3 className="text-lg font-semibold mb-2 border-b pb-1">Legal Links</h3>
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-1">Terms of Service URL</label>
                  <input type="text" value={formData.termsUrl} onChange={(e) => setFormData({ ...formData, termsUrl: e.target.value })} className="w-full border rounded px-3 py-2" />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-1">Privacy Policy URL</label>
                  <input type="text" value={formData.privacyUrl} onChange={(e) => setFormData({ ...formData, privacyUrl: e.target.value })} className="w-full border rounded px-3 py-2" />
                </div>

                {/* Address */}
                <div className="col-span-2">
                  <h3 className="text-lg font-semibold mb-2 border-b pb-1">Company Address</h3>
                </div>
                <div className="mb-4 col-span-2">
                  <label className="block text-sm font-medium mb-1">Address</label>
                  <input type="text" value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} className="w-full border rounded px-3 py-2" />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-1">City</label>
                  <input type="text" value={formData.city} onChange={(e) => setFormData({ ...formData, city: e.target.value })} className="w-full border rounded px-3 py-2" />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-1">State/Province</label>
                  <input type="text" value={formData.state} onChange={(e) => setFormData({ ...formData, state: e.target.value })} className="w-full border rounded px-3 py-2" />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-1">Country</label>
                  <input type="text" value={formData.country} onChange={(e) => setFormData({ ...formData, country: e.target.value })} className="w-full border rounded px-3 py-2" />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-1">Zip/Postal Code</label>
                  <input type="text" value={formData.zipCode} onChange={(e) => setFormData({ ...formData, zipCode: e.target.value })} className="w-full border rounded px-3 py-2" />
                </div>

                {/* Subscription */}
                <div className="col-span-2">
                  <h3 className="text-lg font-semibold mb-2 border-b pb-1">Subscription Plan</h3>
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-1">Select Plan</label>
                  <select 
                    value={formData.plan || 'starter'} 
                    onChange={(e) => {
                      setFormData({ ...formData, plan: e.target.value });
                    }} 
                    className="w-full border rounded px-3 py-2"
                  >
                    <option value="starter">Starter</option>
                    <option value="professional">Professional</option>
                    <option value="enterprise">Enterprise</option>
                    {subscriptions.map(sub => {
                      const price = sub.pricing?.monthlyPrice || sub.pricing?.monthly || 0;
                      const users = sub.limits?.users || sub.limits?.maxUsers || 1;
                      return (
                        <option key={sub.packageId} value={sub.name}>
                          {sub.name} (${price}/mo - {users} users)
                        </option>
                      );
                    })}
                  </select>
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-1">Max SaaS Apps</label>
                  <input type="number" value={formData.maxSaaS} onChange={(e) => setFormData({ ...formData, maxSaaS: parseInt(e.target.value) })} className="w-full border rounded px-3 py-2" />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-1">Max Tenants</label>
                  <input type="number" value={formData.maxTenants} onChange={(e) => setFormData({ ...formData, maxTenants: parseInt(e.target.value) })} className="w-full border rounded px-3 py-2" />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-1">API Rate Limit</label>
                  <input type="number" value={formData.apiRateLimit} onChange={(e) => setFormData({ ...formData, apiRateLimit: parseInt(e.target.value) })} className="w-full border rounded px-3 py-2" />
                </div>

                {/* Platform Admin */}
                <div className="col-span-2">
                  <h3 className="text-lg font-semibold mb-2 border-b pb-1">Platform Admin Account</h3>
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-1">Admin Name *</label>
                  <input type="text" value={formData.adminName} onChange={(e) => setFormData({ ...formData, adminName: e.target.value })} className="w-full border rounded px-3 py-2" required />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-1">Admin Email *</label>
                  <input type="email" value={formData.adminEmail} onChange={(e) => setFormData({ ...formData, adminEmail: e.target.value })} className="w-full border rounded px-3 py-2" required />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-1">Admin Password *</label>
                  <input type="password" value={formData.adminPassword} onChange={(e) => setFormData({ ...formData, adminPassword: e.target.value })} className="w-full border rounded px-3 py-2" required />
                </div>
              </div>
              <div className="flex justify-end gap-2 mt-6 pt-4 border-t">
                <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 border rounded">Cancel</button>
                <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">Create Platform</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Create/Edit Admin Modal */}
      {showAdminModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">
              {editingAdmin ? 'Edit Admin' : 'Create Platform Admin'}
            </h2>
            <form onSubmit={editingAdmin ? handleUpdateAdmin : handleCreateAdmin}>
              {!editingAdmin && (
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-1">Platform *</label>
                  <select
                    value={adminFormData.paasId}
                    onChange={(e) => setAdminFormData({ ...adminFormData, paasId: e.target.value })}
                    className="w-full border rounded px-3 py-2"
                    required
                  >
                    <option value="">Select a platform...</option>
                    {paasList.map(paas => (
                      <option key={paas.paasId} value={paas.paasId}>
                        {paas.name} ({paas.domain || 'No domain'})
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Name *</label>
                <input
                  type="text"
                  value={adminFormData.name}
                  onChange={(e) => setAdminFormData({ ...adminFormData, name: e.target.value })}
                  className="w-full border rounded px-3 py-2"
                  required
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">
                  Email {editingAdmin ? '' : '*'}
                </label>
                <input
                  type="email"
                  value={adminFormData.email}
                  onChange={(e) => setAdminFormData({ ...adminFormData, email: e.target.value })}
                  className="w-full border rounded px-3 py-2"
                  required={!editingAdmin}
                  disabled={editingAdmin}
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">
                  Password {editingAdmin ? '(leave blank to keep current)' : '*'}
                </label>
                <input
                  type="password"
                  value={adminFormData.password}
                  onChange={(e) => setAdminFormData({ ...adminFormData, password: e.target.value })}
                  className="w-full border rounded px-3 py-2"
                  required={!editingAdmin}
                />
              </div>
              {editingAdmin && (
                <div className="mb-4">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={editingAdmin.isActive}
                      onChange={() => handleAdminStatusToggle(editingAdmin.userId, editingAdmin.isActive)}
                    />
                    <span>Active</span>
                  </label>
                </div>
              )}
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => { setShowAdminModal(false); setEditingAdmin(null); setAdminFormData({ email: '', password: '', name: '', paasId: '' }); }}
                  className="px-4 py-2 border rounded"
                >
                  Cancel
                </button>
                <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded">
                  {editingAdmin ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Subscription Modal */}
      {showSubscriptionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto py-6">
          <div className="bg-white p-6 rounded-lg w-full max-w-2xl my-6 max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">
              {editingSubscription ? 'Edit Subscription' : 'Create Subscription'}
            </h2>
            <form onSubmit={handleSubscriptionSubmit}>
              <div className="grid grid-cols-2 gap-4">
                <div className="mb-4 col-span-2">
                  <label className="block text-sm font-medium mb-1">Name *</label>
                  <input type="text" name="name" defaultValue={editingSubscription?.name} className="w-full border rounded px-3 py-2" required />
                </div>
                <div className="mb-4 col-span-2">
                  <label className="block text-sm font-medium mb-1">Description</label>
                  <textarea name="description" defaultValue={editingSubscription?.description} className="w-full border rounded px-3 py-2" rows="2" />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-1">Type *</label>
                  <select name="type" defaultValue={editingSubscription?.type || 'software'} className="w-full border rounded px-3 py-2" required>
                    <option value="software">Software</option>
                    <option value="template">Template</option>
                    <option value="content">Content</option>
                    <option value="plugin">Plugin</option>
                    <option value="theme">Theme</option>
                    <option value="addon">Addon</option>
                  </select>
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-1">Category</label>
                  <input type="text" name="category" defaultValue={editingSubscription?.category} className="w-full border rounded px-3 py-2" />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-1">Version</label>
                  <input type="text" name="version" defaultValue={editingSubscription?.version || '1.0.0'} className="w-full border rounded px-3 py-2" />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-1">Currency</label>
                  <select name="currency" defaultValue={editingSubscription?.pricing?.currency || 'USD'} className="w-full border rounded px-3 py-2">
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                    <option value="GBP">GBP</option>
                    <option value="INR">INR</option>
                  </select>
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-1">Monthly Price</label>
                  <input type="number" name="monthlyPrice" step="0.01" defaultValue={editingSubscription?.pricing?.monthlyPrice || 0} className="w-full border rounded px-3 py-2" />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-1">Yearly Price</label>
                  <input type="number" name="yearlyPrice" step="0.01" defaultValue={editingSubscription?.pricing?.yearlyPrice || 0} className="w-full border rounded px-3 py-2" />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-1">Max Users</label>
                  <input type="number" name="maxUsers" defaultValue={editingSubscription?.limits?.users || 1} className="w-full border rounded px-3 py-2" />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-1">Max Storage (MB)</label>
                  <input type="number" name="maxStorage" defaultValue={editingSubscription?.limits?.storage || 1024} className="w-full border rounded px-3 py-2" />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-1">Max API Calls</label>
                  <input type="number" name="maxApiCalls" defaultValue={editingSubscription?.limits?.apiCalls || 1000} className="w-full border rounded px-3 py-2" />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-1">Max Custom Domains</label>
                  <input type="number" name="maxDomains" defaultValue={editingSubscription?.limits?.customDomains || 0} className="w-full border rounded px-3 py-2" />
                </div>
                <div className="mb-4 col-span-2">
                  <label className="block text-sm font-medium mb-1">Features (comma-separated)</label>
                  <input type="text" name="features" defaultValue={editingSubscription?.features?.join(', ')} placeholder="Feature 1, Feature 2" className="w-full border rounded px-3 py-2" />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-1">Sort Order</label>
                  <input type="number" name="sortOrder" defaultValue={editingSubscription?.sortOrder || 0} className="w-full border rounded px-3 py-2" />
                </div>
                <div className="mb-4">
                  <label className="flex items-center gap-2">
                    <input type="checkbox" name="isActive" defaultChecked={editingSubscription?.isActive !== false} />
                    <span>Active</span>
                  </label>
                  <label className="flex items-center gap-2 mt-2">
                    <input type="checkbox" name="isFeatured" defaultChecked={editingSubscription?.isFeatured} />
                    <span>Featured</span>
                  </label>
                  <label className="flex items-center gap-2 mt-2">
                    <input type="checkbox" name="isFree" defaultChecked={editingSubscription?.pricing?.isFree} />
                    <span>Free Plan</span>
                  </label>
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <button type="button" onClick={() => { setShowSubscriptionModal(false); setEditingSubscription(null); }} className="px-4 py-2 border rounded">Cancel</button>
                <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded">
                  {editingSubscription ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit PAAS Modal */}
      {showEditModal && editingPAAS && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto py-6">
          <div className="bg-white p-6 rounded-lg w-full max-w-4xl my-6 max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">Edit Platform</h2>
            <form onSubmit={handleUpdatePAAS}>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <h3 className="text-lg font-semibold mb-2 border-b pb-1">Basic Information</h3>
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-1">Platform Name *</label>
                  <input 
                    type="text" 
                    value={editingPAAS.name || ''} 
                    onChange={(e) => setEditingPAAS({ ...editingPAAS, name: e.target.value })} 
                    className="w-full border rounded px-3 py-2" 
                    required 
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-1">Company Name</label>
                  <input 
                    type="text" 
                    value={editingPAAS.companyName || ''} 
                    onChange={(e) => setEditingPAAS({ ...editingPAAS, companyName: e.target.value })} 
                    className="w-full border rounded px-3 py-2" 
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-1">Contact Email *</label>
                  <input 
                    type="email" 
                    value={editingPAAS.contactEmail || ''} 
                    onChange={(e) => setEditingPAAS({ ...editingPAAS, contactEmail: e.target.value })} 
                    className="w-full border rounded px-3 py-2" 
                    required 
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-1">Support Email</label>
                  <input 
                    type="email" 
                    value={editingPAAS.supportEmail || ''} 
                    onChange={(e) => setEditingPAAS({ ...editingPAAS, supportEmail: e.target.value })} 
                    className="w-full border rounded px-3 py-2" 
                  />
                </div>
                <div className="mb-4 col-span-2">
                  <label className="block text-sm font-medium mb-1">Description</label>
                  <textarea 
                    value={editingPAAS.description || ''} 
                    onChange={(e) => setEditingPAAS({ ...editingPAAS, description: e.target.value })} 
                    className="w-full border rounded px-3 py-2" 
                    rows="2" 
                  />
                </div>

                <div className="col-span-2">
                  <h3 className="text-lg font-semibold mb-2 border-b pb-1">Branding</h3>
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-1">Primary Color</label>
                  <input 
                    type="color" 
                    value={editingPAAS.primaryColor || '#1976d2'} 
                    onChange={(e) => setEditingPAAS({ ...editingPAAS, primaryColor: e.target.value })} 
                    className="w-full h-10 border rounded" 
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-1">Domain</label>
                  <input 
                    type="text" 
                    value={editingPAAS.domain || ''} 
                    onChange={(e) => setEditingPAAS({ ...editingPAAS, domain: e.target.value })} 
                    className="w-full border rounded px-3 py-2" 
                    placeholder="platform.example.com"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-1">Logo</label>
                  {editingPAAS.logo && (
                    <div className="mb-2">
                      <img src={getLogoUrl(editingPAAS.logo)} alt="Current Logo" className="h-16 object-contain" />
                      <span className="text-xs text-gray-500 block">Current logo</span>
                    </div>
                  )}
                  <input 
                    type="file" 
                    accept="image/png,image/jpeg,image/svg+xml"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        const preview = URL.createObjectURL(file);
                        setEditingPAAS({ ...editingPAAS, logoFile: file, logoFilePreview: preview });
                      }
                    }} 
                    className="w-full border rounded px-3 py-2 text-sm" 
                  />
                  {editingPAAS.logoFilePreview && (
                    <img src={editingPAAS.logoFilePreview} alt="New logo preview" className="mt-2 h-16 object-contain" />
                  )}
                </div>

                <div className="col-span-2">
                  <h3 className="text-lg font-semibold mb-2 border-b pb-1">Legal Links</h3>
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-1">Terms of Service URL</label>
                  <input 
                    type="text" 
                    value={editingPAAS.termsUrl || ''} 
                    onChange={(e) => setEditingPAAS({ ...editingPAAS, termsUrl: e.target.value })} 
                    className="w-full border rounded px-3 py-2" 
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-1">Privacy Policy URL</label>
                  <input 
                    type="text" 
                    value={editingPAAS.privacyUrl || ''} 
                    onChange={(e) => setEditingPAAS({ ...editingPAAS, privacyUrl: e.target.value })} 
                    className="w-full border rounded px-3 py-2" 
                  />
                </div>

                <div className="col-span-2">
                  <h3 className="text-lg font-semibold mb-2 border-b pb-1">Company Address</h3>
                </div>
                <div className="mb-4 col-span-2">
                  <label className="block text-sm font-medium mb-1">Address</label>
                  <input 
                    type="text" 
                    value={editingPAAS.address?.street || ''} 
                    onChange={(e) => setEditingPAAS({ ...editingPAAS, address: { ...editingPAAS.address, street: e.target.value } })} 
                    className="w-full border rounded px-3 py-2" 
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-1">City</label>
                  <input 
                    type="text" 
                    value={editingPAAS.address?.city || ''} 
                    onChange={(e) => setEditingPAAS({ ...editingPAAS, address: { ...editingPAAS.address, city: e.target.value } })} 
                    className="w-full border rounded px-3 py-2" 
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-1">State/Province</label>
                  <input 
                    type="text" 
                    value={editingPAAS.address?.state || ''} 
                    onChange={(e) => setEditingPAAS({ ...editingPAAS, address: { ...editingPAAS.address, state: e.target.value } })} 
                    className="w-full border rounded px-3 py-2" 
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-1">Country</label>
                  <input 
                    type="text" 
                    value={editingPAAS.address?.country || ''} 
                    onChange={(e) => setEditingPAAS({ ...editingPAAS, address: { ...editingPAAS.address, country: e.target.value } })} 
                    className="w-full border rounded px-3 py-2" 
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-1">Zip/Postal Code</label>
                  <input 
                    type="text" 
                    value={editingPAAS.address?.postalCode || ''} 
                    onChange={(e) => setEditingPAAS({ ...editingPAAS, address: { ...editingPAAS.address, postalCode: e.target.value } })} 
                    className="w-full border rounded px-3 py-2" 
                  />
                </div>

                <div className="col-span-2">
                  <h3 className="text-lg font-semibold mb-2 border-b pb-1">Subscription Plan</h3>
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-1">Plan</label>
                  <select 
                    value={editingPAAS.subscription?.plan || editingPAAS.plan || 'starter'} 
                    onChange={(e) => setEditingPAAS({ ...editingPAAS, subscription: { ...editingPAAS.subscription, plan: e.target.value } })} 
                    className="w-full border rounded px-3 py-2"
                  >
                    <option value="starter">Starter</option>
                    <option value="professional">Professional</option>
                    <option value="enterprise">Enterprise</option>
                  </select>
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-1">Max SaaS Apps</label>
                  <input 
                    type="number" 
                    value={editingPAAS.maxSaaS || editingPAAS.features?.maxSaaS || 10} 
                    onChange={(e) => setEditingPAAS({ ...editingPAAS, maxSaaS: parseInt(e.target.value) })} 
                    className="w-full border rounded px-3 py-2" 
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-1">Max Tenants</label>
                  <input 
                    type="number" 
                    value={editingPAAS.maxTenants || editingPAAS.features?.maxTenants || 100} 
                    onChange={(e) => setEditingPAAS({ ...editingPAAS, maxTenants: parseInt(e.target.value) })} 
                    className="w-full border rounded px-3 py-2" 
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-1">API Rate Limit</label>
                  <input 
                    type="number" 
                    value={editingPAAS.apiRateLimit || 1000} 
                    onChange={(e) => setEditingPAAS({ ...editingPAAS, apiRateLimit: parseInt(e.target.value) })} 
                    className="w-full border rounded px-3 py-2" 
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2 mt-6 pt-4 border-t">
                <button type="button" onClick={() => { setShowEditModal(false); setEditingPAAS(null); }} className="px-4 py-2 border rounded">Cancel</button>
                <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">Save Changes</button>
              </div>
            </form>
          </div>
        </div>
      )}
      
       {showTopupModal && (
         <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto py-6">
           <div className="bg-white p-6 rounded-lg w-full max-w-md my-6">
             <h2 className="text-xl font-bold mb-4">Top Up Credits</h2>
             <div className="mb-4">
               <label className="block text-sm font-medium mb-2">Select Credit Pack</label>
               <select 
                 id="topup-amount" 
                 className="w-full border rounded px-3 py-2" 
                 value={topupAmount} 
                 onChange={(e) => setTopupAmount(parseInt(e.target.value))}
               >
                 <option value={500}>₹500 - Intro Pack</option>
                 <option value={5000}>₹5,000 - Basic Pack</option>
                 <option value={10000}>₹10,000 - Bronze Pack</option>
                 <option value={50000}>₹50,000 - Silver Pack</option>
                 <option value={100000}>₹100,000 - Gold Pack</option>
                 <option value={1000000}>₹1,000,000 - Platinum Pack</option>
               </select>
             </div>
             <div className="mb-4">
               <label className="block text-sm font-medium mb-2">Payment Method</label>
               <div className="grid grid-cols-2 gap-2">
                 <button 
                   type="button" 
                   className="p-3 border rounded text-center hover:bg-gray-50" 
                   onClick={() => handleTopup('razorpay')}
                 >💳 Razorpay</button>
                 <button 
                   type="button" 
                   className="p-3 border rounded text-center hover:bg-gray-50" 
                   onClick={() => handleTopup('stripe')}
                 >💳 Stripe</button>
               </div>
             </div>
             <div className="flex gap-2">
               <button 
                 onClick={() => setShowTopupModal(false)} 
                 className="bg-gray-600 text-white px-4 py-2 rounded"
               >Cancel</button>
             </div>
           </div>
         </div>
       )}

        </div>
      </div>
    </div>
  );
}